import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Plus, Pencil, Trash2, CalendarIcon, Check, Clock, ChevronLeft, ChevronRight, Printer, Filter, CalendarDays } from "lucide-react";
import { format, parseISO, isToday, isTomorrow, isYesterday, addDays, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addWeeks, subWeeks } from "date-fns";
import { tr } from "date-fns/locale";



const Agenda = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("default"); // default, week, all
  // Varsayılan: Dün'den başla, bugünden 10 gün sonraya kadar göster
  const [currentWeekStart, setCurrentWeekStart] = useState(subDays(new Date(), 1));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    description: "",
    reminder: false,
    completed: false,
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/agenda`);
      setItems(response.data);
    } catch (error) {
      console.error("Failed to fetch agenda items:", error);
      toast.error("Ajanda öğeleri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.date) {
      toast.error("Başlık ve tarih zorunludur");
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/agenda/${editingItem.id}`, formData);
        toast.success("Görev güncellendi");
      } else {
        await axios.post(`${API}/agenda`, formData);
        toast.success("Görev eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error("Failed to save item:", error);
      toast.error("İşlem başarısız");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      date: item.date,
      time: item.time || "",
      description: item.description || "",
      reminder: item.reminder,
      completed: item.completed,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu görevi silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/agenda/${id}`);
      toast.success("Görev silindi");
      fetchItems();
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const toggleComplete = async (item) => {
    try {
      await axios.put(`${API}/agenda/${item.id}`, {
        ...item,
        completed: !item.completed,
      });
      fetchItems();
    } catch (error) {
      console.error("Failed to update item:", error);
      toast.error("Güncelleme başarısız");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      title: "",
      date: new Date().toISOString().split("T")[0],
      time: "",
      description: "",
      reminder: false,
      completed: false,
    });
  };

  const handleAddForDate = (date) => {
    setFormData({
      ...formData,
      date: format(date, "yyyy-MM-dd"),
    });
    setDialogOpen(true);
  };

  // Get days range: dün'den bugünden 10 gün sonraya kadar
  const weekEnd = addDays(new Date(), 10);
  const weekDays = eachDayOfInterval({ start: currentWeekStart, end: weekEnd });

  // Group items by date
  const itemsByDate = items.reduce((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {});

  const goToPrevWeek = () => setCurrentWeekStart(subDays(currentWeekStart, 7));
  const goToNextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7));
  const goToToday = () => setCurrentWeekStart(subDays(new Date(), 1));

  const getDateLabel = (date) => {
    if (isToday(date)) return "Bugün";
    if (isTomorrow(date)) return "Yarın";
    return format(date, "d MMMM", { locale: tr });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4 md:space-y-6 print:space-y-4" data-testid="agenda-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ajanda
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Görevlerinizi ve hatırlatmalarınızı yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-agenda-item-btn">
              <Plus className="h-5 w-5 mr-2" />
              Yeni Görev
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Görev Düzenle" : "Yeni Görev"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Başlık *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Görev başlığı"
                  data-testid="agenda-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tarih *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="agenda-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Saat</Label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Görev açıklaması"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="reminder"
                    checked={formData.reminder}
                    onCheckedChange={(checked) => setFormData({ ...formData, reminder: checked })}
                  />
                  <Label htmlFor="reminder" className="cursor-pointer">Hatırlatıcı</Label>
                </div>
                {editingItem && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="completed"
                      checked={formData.completed}
                      onCheckedChange={(checked) => setFormData({ ...formData, completed: checked })}
                    />
                    <Label htmlFor="completed" className="cursor-pointer">Tamamlandı</Label>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="agenda-save-btn">
                  {editingItem ? "Güncelle" : "Kaydet"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardHeader className="pb-2 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToPrevWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Bugün
              </Button>
              <Button variant="outline" size="sm" onClick={goToNextWeek}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {format(currentWeekStart, "d MMMM yyyy", { locale: tr })} - {format(weekEnd, "d MMMM yyyy", { locale: tr })}
            </h2>
          </div>
        </CardHeader>
      </Card>

      {/* Days List */}
      <div className="space-y-3">
        {weekDays.map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const dayItems = itemsByDate[dateStr] || [];
          const isCurrentDay = isToday(day);
          const dayName = format(day, "EEEE", { locale: tr });

          return (
            <Card 
              key={dateStr} 
              className={`transition-colors ${isCurrentDay ? "ring-2 ring-blue-500 bg-blue-50/30" : ""}`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Date Column */}
                <div className={`p-4 md:w-48 flex-shrink-0 border-b md:border-b-0 md:border-r ${isCurrentDay ? "bg-blue-100/50" : "bg-slate-50"}`}>
                  <div className="flex md:flex-col items-center md:items-start gap-2 md:gap-1">
                    <div className={`text-3xl md:text-4xl font-bold ${isCurrentDay ? "text-blue-600" : "text-slate-700"}`}>
                      {format(day, "d")}
                    </div>
                    <div className="flex flex-col">
                      <span className={`text-sm font-medium ${isCurrentDay ? "text-blue-600" : "text-slate-600"}`}>
                        {dayName}
                      </span>
                      <span className="text-xs text-slate-500">
                        {format(day, "MMMM yyyy", { locale: tr })}
                      </span>
                    </div>
                    {isCurrentDay && (
                      <span className="ml-auto md:ml-0 md:mt-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        Bugün
                      </span>
                    )}
                  </div>
                </div>

                {/* Tasks Column */}
                <div className="flex-1 p-4">
                  {dayItems.length > 0 ? (
                    <div className="space-y-2">
                      {dayItems.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            item.completed 
                              ? "bg-slate-50 border-slate-200" 
                              : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <button
                            onClick={() => toggleComplete(item)}
                            className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                              item.completed
                                ? "bg-emerald-500 border-emerald-500 text-white"
                                : "border-slate-300 hover:border-slate-400"
                            }`}
                          >
                            {item.completed && <Check className="h-3 w-3" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${item.completed ? "text-slate-400 line-through" : "text-slate-900"}`}>
                                {item.title}
                              </p>
                              {item.time && (
                                <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                  <Clock className="h-3 w-3" />
                                  {item.time}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-slate-500 mt-1 line-clamp-1">{item.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} className="h-8 w-8 p-0">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-16 text-slate-400 text-sm">
                      <span>Görev yok</span>
                    </div>
                  )}
                  
                  {/* Add Task Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddForDate(day)}
                    className="mt-2 text-slate-500 hover:text-slate-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Görev Ekle
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Agenda;
